/**
 * Reference content for the API Keys documentation panel.
 *
 * Example responses mirror the shapes the public API actually returns; keep
 * them in step with integrations/api/v1/serializers.py in the backend.
 */

export const AUTH_HEADER = 'Authorization: Api-Key pb_live_<prefix>_<secret>';

export const RATE_LIMIT = '1000 requests per hour, per key';

export const MAX_PAGE_SIZE = 200;

const DEMO_ROW = `{
  "id": "8f1c2d3e-4b5a-6789-abcd-ef0123456789",
  "status": "ENDED",
  "result": "success",
  "requested_from": "PRODUCTPAGE",
  "is_live_meeting": false,
  "is_sales_demo": false,
  "created_at": "2026-08-02T14:05:11Z",
  "last_activity_at": "2026-08-02T15:40:52Z",
  "accepted_at": "2026-08-02T14:09:03Z",
  "started_at": "2026-08-02T15:00:44Z",
  "ended_at": "2026-08-02T15:28:10Z",
  "cancelled_at": null,
  "scheduled_for": { "date": "2026-08-02", "time": "15:00:00" },
  "product": {
    "id": "b21e...",
    "identifier": 7412589630,
    "name": "Sequoia Barn Door",
    "handle": "sequoia-barn-door",
    "image": "https://cdn.shopify.com/...",
    "price": "1299.000",
    "currency_code": "USD",
    "tag": "Doors"
  },
  "hosts": [
    { "id": "3c9d...", "first_name": "Dana", "last_name": "Reyes",
      "email": "dana@example.com" }
  ],
  "guests": [
    { "id": "77aa...", "full_name": "Sam Carter", "email": "sam@example.com",
      "attended": true, "is_extra_guest": false }
  ],
  "rating": 90,
  "average_rating": 4.5,
  "guest_review_note": "Really helpful walkthrough.",
  "coupon": "DEMO-4F2A",
  "technical_difficulties": false,
  "has_discussed_business": true
}`;

const INSIGHTS_BODY = `{
  "demo_id": "8f1c2d3e-4b5a-6789-abcd-ef0123456789",
  "status": "ENDED",
  "ended_at": "2026-08-02T15:28:10Z",
  "processing_ended_at": "2026-08-02T15:40:52Z",

  "sentiment_status": "ready",
  "sentiment": {
    "summary": "The guest asked about install clearance and shipping lead
                time, and reacted well to the soft-close demo.",
    "host_sentiment": "Positive",
    "guest_sentiment": "Neutral",
    "overall_meeting_sentiment": "Positive",
    "conversation_tone": "Friendly",
    "valid_discussion": true,
    "technical_difficulties": false
  },

  "takeaways": {
    "summary": "The guest asked about install clearance and shipping lead
                time, and reacted well to the soft-close demo.",
    "pros": [
      { "id": "1a2b...", "text": "Soft-close hardware feels premium",
        "verbatim": "that soft close is really nice", "user_type": "GUEST" }
    ],
    "cons": [
      { "id": "9f8e...", "text": "Lead time is too long",
        "verbatim": "six weeks is a while to wait", "user_type": "GUEST" }
    ],
    "questions": [
      { "id": "44cc...", "text": "How much wall clearance is needed?",
        "verbatim": "how much room do I need on the wall",
        "question_type": "audio" }
    ]
  },

  "conversion": {
    "likelihood_percentage": 72.5,
    "action_plans": {
      "host_action_plan": "Send the install clearance diagram.",
      "guest_action_plans": [
        { "user_id": "77aa...", "conversion_likelihood": 72.5,
          "action_plan": "Follow up with lead-time options." }
      ]
    },
    "status": "COMPLETED"
  },

  "feedback": { "rating": 90, "guest_review_note": "Really helpful walkthrough." },

  "flags": {
    "technical_difficulties": false,
    "has_discussed_business": true,
    "has_discussed_business_status": "FINISHED",
    "is_best_of": false
  },

  "processing_status": {
    "questions": "COMPLETED",
    "host_pros_and_cons": "COMPLETED",
    "guest_pros_and_cons": "COMPLETED",
    "conversion_analysis": "COMPLETED"
  }
}`;

export const ENDPOINTS = [
  {
    id: 'meta',
    method: 'GET',
    path: '/api/v1/public/meta/',
    summary: 'Verify a key and see which store it belongs to.',
    detail:
      'The cheapest way to confirm a credential works. Call this first when ' +
      'setting up an integration.',
    request: 'GET /api/v1/public/meta/',
    response: `{
  "key_name": "Warehouse sync",
  "key_prefix": "a1b2c3d4e5f6",
  "scopes": ["demos:read", "insights:read", "products:read", "payouts:read"],
  "store_name": "Murphy Door",
  "store_identifier": 7412589630,
  "created_at": "2026-07-01T10:00:00Z",
  "last_used_at": "2026-08-11T09:30:00Z",
  "expires_at": null,
  "rate_limit": "1000/hour"
}`,
  },
  {
    id: 'demos',
    method: 'GET',
    path: '/api/v1/public/demos/',
    summary: 'Demo records, newest first.',
    detail:
      'Filters: status, start_date, end_date (YYYY-MM-DD), product_id, ' +
      'host_id, updated_since. Add include=insights to embed each demo’s ' +
      'insight payload. Paginate with limit and offset ' +
      `(max ${MAX_PAGE_SIZE} per page).`,
    request:
      'GET /api/v1/public/demos/?status=ENDED&start_date=2026-08-01&limit=50',
    response: `{
  "count": 128,
  "next": "https://.../api/v1/public/demos/?limit=50&offset=50",
  "previous": null,
  "results": [
${DEMO_ROW.split('\n')
  .map((line) => `    ${line}`)
  .join('\n')}
  ]
}`,
  },
  {
    id: 'demo-insights',
    method: 'GET',
    path: '/api/v1/public/demos/{id}/insights/',
    summary: 'Sentiment, takeaways, pros and cons, conversion likelihood.',
    detail:
      'sentiment_status is "ready", "pending" (the AI pipeline has not ' +
      'finished yet) or "failed". Treat "pending" as not-yet-available rather ' +
      'than neutral — insights land a while after a demo ends.',
    request: 'GET /api/v1/public/demos/8f1c2d3e-.../insights/',
    response: INSIGHTS_BODY,
  },
  {
    id: 'pros-cons',
    method: 'GET',
    path: '/api/v1/public/insights/pros-cons/',
    summary: 'Store-wide pros and cons with how often each came up.',
    detail:
      'Filters: type=pros|cons, user_type=HOST|GUEST, product_category. ' +
      'Aggregated across every demo, so this is the view for "what do ' +
      'customers keep saying about this catalog".',
    request: 'GET /api/v1/public/insights/pros-cons/?user_type=GUEST',
    response: `{
  "pros": [
    { "id": "1a2b...", "text": "Soft-close hardware feels premium",
      "user_type": "GUEST", "product_category": "Doors", "count": 34 }
  ],
  "cons": [
    { "id": "9f8e...", "text": "Lead time is too long",
      "user_type": "GUEST", "product_category": "Doors", "count": 12 }
  ]
}`,
  },
  {
    id: 'questions',
    method: 'GET',
    path: '/api/v1/public/insights/questions/',
    summary: 'Questions guests asked, most frequent first.',
    detail: 'Useful for FAQ and product-copy gaps.',
    request: 'GET /api/v1/public/insights/questions/?limit=20',
    response: `{
  "count": 87,
  "next": null,
  "previous": null,
  "results": [
    { "id": "44cc...", "text": "How much wall clearance is needed?", "count": 41 },
    { "id": "55dd...", "text": "Do you ship to Canada?", "count": 29 }
  ]
}`,
  },
  {
    id: 'products',
    method: 'GET',
    path: '/api/v1/public/products/',
    summary: 'Products with demo counts and average rating.',
    detail: 'average_rating is 0–5 stars, or null when nothing is rated yet.',
    request: 'GET /api/v1/public/products/',
    response: `{
  "count": 42,
  "next": null,
  "previous": null,
  "results": [
    {
      "id": "b21e...",
      "identifier": 7412589630,
      "name": "Sequoia Barn Door",
      "handle": "sequoia-barn-door",
      "image": "https://cdn.shopify.com/...",
      "price": "1299.000",
      "currency_code": "USD",
      "tag": "Doors",
      "demo_count": 37,
      "completed_demo_count": 31,
      "average_rating": 4.4
    }
  ]
}`,
  },
  {
    id: 'payouts',
    method: 'GET',
    path: '/api/v1/public/payouts/',
    summary: 'Commission and payout records.',
    detail: 'Filters: start_date, end_date, against the demo’s end date.',
    request: 'GET /api/v1/public/payouts/?start_date=2026-07-01',
    response: `{
  "count": 96,
  "next": null,
  "previous": null,
  "results": [
    {
      "id": "cc31...",
      "demo_id": "8f1c2d3e-...",
      "product_name": "Sequoia Barn Door",
      "demo_ended_at": "2026-08-02T15:28:10Z",
      "earning": "25.00",
      "earning_paid": "2026-08-09T00:00:00Z",
      "sales_commission": "64.95",
      "sales_commission_paid": null,
      "sales_commission_order_id": "5501234567890",
      "sales_commission_order_status": "fulfilled",
      "sales_commission_obtained_date": "2026-08-04T18:22:00Z",
      "sales_commission_fulfilled_date": "2026-08-06T11:02:00Z",
      "sales_delivered": true,
      "created_at": "2026-08-02T15:28:12Z",
      "updated_at": "2026-08-06T11:02:00Z"
    }
  ]
}`,
  },
];

export const SYNC_EXAMPLE = `# 1. First run: pull everything, embedding insights so there is
#    no follow-up call per demo.
GET /api/v1/public/demos/?include=insights&limit=200

# 2. Record the newest last_activity_at you processed.
checkpoint = max(row.last_activity_at for row in results)

# 3. Every run after that, ask only for what changed.
GET /api/v1/public/demos/?updated_since={checkpoint}&include=insights&limit=200`;

export const ERRORS = [
  ['200', 'Success.'],
  ['400', 'A parameter could not be honored, e.g. a malformed updated_since.'],
  ['401', 'Missing, malformed, unknown, revoked or expired key.'],
  ['403', 'The key is valid but lacks the scope for that endpoint.'],
  ['404', 'No such record in your store.'],
  ['429', 'Rate limit exceeded. Back off and retry.'],
];

export const SCOPES = [
  ['demos:read', 'demos/ and demos/{id}/insights/'],
  ['insights:read', 'insights/pros-cons/ and insights/questions/'],
  ['products:read', 'products/'],
  ['payouts:read', 'payouts/'],
];
